import { apiClient } from './client'
import type { Exercise } from './exercises'
import type { PlacementLevel, PlacementSkill } from './placementBank'

/**
 * Тест уровня и языковой профиль ученика.
 *
 * Профиль принадлежит человеку, а не договору: ученик читает свой, учитель —
 * профили учеников по действующему договору. Прогон теста сдают только за себя,
 * поэтому у его маршрутов ученика в адресе нет вовсе.
 */

export type LearningGoal =
  | 'interview'
  | 'travel'
  | 'exam'
  | 'work'
  | 'relocation'
  | 'speaking_practice'
  | 'school'
  | 'from_scratch'

export const LEARNING_GOALS: LearningGoal[] = [
  'interview',
  'travel',
  'exam',
  'work',
  'relocation',
  'speaking_practice',
  'school',
  'from_scratch',
]

/** Цели, у которых есть срок: у «практики» и «с нуля» его не существует. */
export const GOALS_WITH_DEADLINE: LearningGoal[] = ['interview', 'exam', 'relocation', 'school']

/** Чем измерен уровень — мера доверия к числу, а не украшение карточки. */
export type LanguageProfileSource = 'self_assessment' | 'test' | 'teacher'

export type PlacementStage = 'goals' | 'adaptive' | 'probes'

export type PlacementRunStatus = 'in_progress' | 'completed' | 'abandoned'

/** Шкала профиля шире шкалы банка: A0 значит «ещё не A1». */
export type ProfileLevel = 'A0' | PlacementLevel

export const PROFILE_LEVELS: ProfileLevel[] = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']

/** Навыки профиля в порядке показа: сначала закрытые, потом пробы. */
export const PROFILE_SKILLS: PlacementSkill[] = [
  'grammar_vocabulary',
  'reading',
  'listening',
  'writing',
  'speaking',
]

/**
 * Чем кончилось измерение одного навыка.
 *
 * Три разных исхода вместо одного «не измеряли»: уровень известен точно, известна
 * только граница (тест прервали) или мерить нечем — заданий навыка нет в банке.
 */
export type SkillMeasurement = {
  level: ProfileLevel | null
  /** «Не ниже»: ступень взята, но выше уточнить не успели. */
  floor: ProfileLevel | null
  /** «Не выше»: ступень провалена, ниже уточнить не успели. */
  ceiling: ProfileLevel | null
  state: 'pending' | 'measured' | 'bounded' | 'not_testable'
}

export type StudentLanguageProfile = {
  id: number
  user_id: number
  goal: LearningGoal | null
  goal_details: string | null
  goal_deadline: string | null
  days_per_week: number | null
  minutes_per_day: number | null
  interests: string[] | null
  overall_level: ProfileLevel | null
  /** Навык → уровень; null означает «не измеряли», а не «не справился». */
  skill_levels: Record<string, ProfileLevel | null>
  level_source: LanguageProfileSource | null
  /** Чем учитель объяснил свою правку уровня; хранится последний. */
  level_comment?: string | null
  /** Разбор по навыкам; отсутствует у профилей, измеренных до его появления. */
  skill_measurements?: Record<string, SkillMeasurement> | null
  assessed_at: string | null
  /** Каким ученик считает себя сам — отдельно от измеренного уровня. */
  self_assessed_level: ProfileLevel | null
  has_level: boolean
  /**
   * Результат последнего теста, когда он спорит с профилем.
   *
   * Правка учителя сильнее теста и держится на весь профиль: поправив письмо,
   * учитель закрывает профиль от будущих прогонов. Поле показывает, что
   * намерил последний тест, — профиль от него не меняется.
   */
  last_test: { skill_levels: Record<string, ProfileLevel>; completed_at: string | null } | null
  updated_at: string | null
}

/**
 * Задание теста глазами сдающего — уже не `Exercise`.
 *
 * Сервер отдаёт только тип, формулировку и очищенный контент. Ни сложности,
 * ни статуса, ни признака системного задания здесь нет: в банке сложность
 * однозначно следует из ступени CEFR, и по ней ученик читал бы полосу, на
 * которую его завёл алгоритм. Идентификатора задания нет по той же причине —
 * позиции банка пронумерованы по возрастанию ступени.
 */
export type PlacementExercise = Pick<Exercise, 'type' | 'instructions' | 'content'>

export type PlacementRunItem = {
  id: number
  block_number: number
  answered: boolean
  exercise_run_id: number | null
  /** Задание БЕЗ эталонов: контент проходит через санитайзер на сервере. */
  exercise: PlacementExercise
}

export type PlacementRun = {
  id: number
  stage: PlacementStage
  status: PlacementRunStatus
  block_number: number
  self_assessment: Record<string, unknown> | null
  /** Приходит только у завершённого прогона: промежуточная оценка меняет поведение. */
  computed_levels: Record<string, ProfileLevel | null> | null
  /** Разбор по навыкам закрытого прогона: уровень, граница или «нечем мерить». */
  skill_measurements?: Record<string, SkillMeasurement> | null
  /** Верных из выданных по навыкам — объяснение уровня без выдачи ключей. */
  skill_scores?: Record<string, SkillScore> | null
  started_at: string
  completed_at: string | null
  items?: PlacementRunItem[]
}

export type PlacementState = {
  run: PlacementRun | null
  /** Раньше этого момента новый прогон не начать; null — можно сейчас. */
  retest_available_at: string | null
}

export type SelfAssessmentPayload = {
  goal?: LearningGoal | null
  goal_details?: string | null
  goal_deadline?: string | null
  days_per_week?: number | null
  minutes_per_day?: number | null
  interests?: string[]
  self_assessed_level?: ProfileLevel | null
}

export type ProfileGoalPayload = Omit<SelfAssessmentPayload, 'self_assessed_level'>

export type ProfileCorrectionPayload = {
  overall_level?: ProfileLevel | null
  skill_levels?: Record<string, ProfileLevel | null>
  comment?: string | null
}

/**
 * Проба письма или говорения глазами учителя.
 *
 * Автопроверки у проб нет, а в общую очередь проверки они не идут: очередь
 * строится по связке «учитель–попытка», а тест ученик проходит до того, как у
 * него появился учитель. Учитель читает пробу в карточке и ставит уровень
 * навыка корректировкой — той же ручкой, что и остальные уровни.
 */
/** Почему проб нет — если их нет. */
export type PlacementProbesState =
  | 'has_probes'
  | 'running'
  | 'finished_without_probes'
  | 'never_tested'

export type PlacementProbe = {
  id: number
  skill: PlacementSkill
  level: ProfileLevel
  answered: boolean
  submitted_at: string | null
  answers: Record<string, unknown> | null
  task: {
    type: string
    instructions: string | null
    content: Record<string, unknown> | null
  }
  /** Чек-лист позиции банка: раздел оценки → что смотреть. */
  evaluation_criteria: Record<string, string> | null
}

/** Верных из выданных по одному навыку. */
export type SkillScore = {
  correct: number
  total: number
}

/**
 * Строка списка пройденных тестов.
 *
 * Без заданий и без ответов: банк один на всех, пересдача разрешена через
 * месяц, и разбор конкретных позиций был бы ключами к следующему тесту.
 */
export type PlacementRunSummary = {
  id: number
  status: PlacementRunStatus
  stage: PlacementStage
  started_at: string
  completed_at: string | null
  /** Сколько блоков заданий выдал адаптивный поиск. */
  blocks: number
  computed_levels: Record<string, ProfileLevel | null> | null
  skill_measurements: Record<string, SkillMeasurement> | null
  self_assessed_level: ProfileLevel | null
  skill_scores: Record<string, SkillScore>
}

/**
 * Задание прогона глазами учителя: что спрашивали и что ответил ученик.
 *
 * Эталонов здесь нет и у учителя: банк один на всех, пересдача идёт по тем же
 * заданиям. Что решено верно, видно из `is_correct`, а не из ключа.
 */
export type PlacementRunItemDetail = {
  id: number
  block_number: number
  skill: string
  level: ProfileLevel
  answered: boolean
  submitted_at: string | null
  /** null у проб письма и речи: автопроверки у них не бывает. */
  score_percent: number | null
  /** Верным считается только полный балл; null там же, где null у балла. */
  is_correct: boolean | null
  answers: Record<string, unknown> | null
  /** Разбор автопроверки по элементам — по нему рендерер красит пропуски. */
  check_details: Record<string, unknown> | null
  task: {
    type: string
    instructions: string | null
    content: Record<string, unknown>
  }
  /** Чек-лист оценки пробы; у закрытых навыков null. */
  evaluation_criteria: Record<string, string> | null
}

/** Прогон целиком — учителю: шапка как в списке плюс задания с ответами. */
export type PlacementRunDetail = Omit<PlacementRunSummary, 'stage'> & {
  stage: PlacementStage
  student: { id: number; name: string }
  items: PlacementRunItemDetail[]
}

export const placementApi = {
  /** Текущий прогон и срок повторной сдачи. */
  async state(): Promise<PlacementState> {
    const { data } = await apiClient.get<{ data: PlacementState }>('/placement/run')
    return data.data
  },

  /** Начать прогон; открытый возвращается как есть, а не заводится второй. */
  async start(): Promise<PlacementRun> {
    const { data } = await apiClient.post<{ data: PlacementRun }>('/placement/run')
    return data.data
  },

  async submitSelfAssessment(runId: number, payload: SelfAssessmentPayload): Promise<PlacementRun> {
    const { data } = await apiClient.post<{ data: PlacementRun }>(
      `/placement/run/${runId}/self-assessment`,
      payload,
    )
    return data.data
  },

  /** Задания текущего блока. Идемпотентно: повторный вызов отдаёт те же. */
  async block(runId: number): Promise<PlacementRunItem[]> {
    const { data } = await apiClient.get<{ data: PlacementRunItem[] }>(`/placement/run/${runId}/block`)
    return data.data
  },

  /**
   * Сдать одно задание.
   *
   * Пустой объект ответов — валидная сдача «не ответил»: иначе ученик, не
   * знающий ни одного ответа, застревал бы на блоке навсегда.
   */
  async submitItem(itemId: number, answers: Record<string, unknown>): Promise<PlacementRunItem> {
    const { data } = await apiClient.post<{ data: PlacementRunItem }>(
      `/placement/items/${itemId}/submit`,
      { answers },
    )
    return data.data
  },

  async advance(runId: number): Promise<PlacementRun> {
    const { data } = await apiClient.post<{ data: PlacementRun }>(`/placement/run/${runId}/advance`)
    return data.data
  },

  async complete(runId: number): Promise<PlacementRun> {
    const { data } = await apiClient.post<{ data: PlacementRun }>(`/placement/run/${runId}/complete`)
    return data.data
  },

  async abandon(runId: number): Promise<PlacementRun> {
    const { data } = await apiClient.delete<{ data: PlacementRun }>(`/placement/run/${runId}`)
    return data.data
  },
}

export const languageProfileApi = {
  async show(userId: number): Promise<StudentLanguageProfile> {
    const { data } = await apiClient.get<{ data: StudentLanguageProfile }>(
      `/users/${userId}/language-profile`,
    )
    return data.data
  },

  /** Правка цели: уровня не касается и пересдачи теста не требует. */
  async updateGoal(userId: number, payload: ProfileGoalPayload): Promise<StudentLanguageProfile> {
    const { data } = await apiClient.patch<{ data: StudentLanguageProfile }>(
      `/users/${userId}/language-profile`,
      payload,
    )
    return data.data
  },

  /** Корректировка уровней учителем: приоритетнее расчётной оценки. */
  async correctLevels(
    userId: number,
    payload: ProfileCorrectionPayload,
  ): Promise<StudentLanguageProfile> {
    const { data } = await apiClient.patch<{ data: StudentLanguageProfile }>(
      `/users/${userId}/language-profile/levels`,
      payload,
    )
    return data.data
  },

  /**
   * Пробы письма и говорения последнего прогона — для оценки человеком.
   *
   * Вместе с ними приходит состояние: пустой список означает три разные вещи,
   * и подписать их одной строкой значило бы соврать учителю про ученика,
   * у которого в профиле уже стоит уровень по тесту.
   */
  async probes(userId: number): Promise<{ items: PlacementProbe[]; state: PlacementProbesState }> {
    const { data } = await apiClient.get<{
      data: PlacementProbe[]
      meta?: { state?: PlacementProbesState }
    }>(`/users/${userId}/language-profile/probes`)

    return { items: data.data, state: data.meta?.state ?? 'never_tested' }
  },

  /**
   * Пройденные тесты ученика, свежий сверху.
   *
   * Профиль показывает одну цифру, а B1 с первой попытки и B1 после трёх
   * заходов за полгода — разные ученики, и первый урок им нужен разный.
   */
  async runs(userId: number): Promise<PlacementRunSummary[]> {
    const { data } = await apiClient.get<{ data: PlacementRunSummary[] }>(
      `/users/${userId}/placement-runs`,
    )

    return data.data
  },

  /**
   * Разбор одного прогона: задания и ответы ученика.
   *
   * Только учителю. Здесь и только здесь видны пробы письма и речи —
   * автопроверки у них нет, в общую очередь проверки они не идут.
   */
  async run(userId: number, runId: number): Promise<PlacementRunDetail> {
    const { data } = await apiClient.get<{ data: PlacementRunDetail }>(
      `/users/${userId}/placement-runs/${runId}`,
    )

    return data.data
  },

}
